/**
 * Module E-mails (bible §11) — contrat BFF ↔ front.
 * Minimisation : le contrat ne transporte JAMAIS le corps d'un message.
 */
import { z } from "zod";
import type { Role } from "./roles";

const Uuid = z.string().uuid();

export const EmailsStatusSchema = z.object({
  /** GOOGLE_CLIENT_ID/SECRET posés côté BFF (console GCP prête). */
  configured: z.boolean(),
  connected: z.boolean(),
  gmailAddress: z.string().nullable(),
  lastSyncAt: z.string().datetime({ offset: true }).nullable(),
  lastError: z.string().nullable(),
  aValider: z.number().int().min(0),
  peutGererBoites: z.boolean(),
  interactions: z.number().int().min(0),
});
export type EmailsStatus = z.infer<typeof EmailsStatusSchema>;

export const EmailInteractionSchema = z.object({
  id: Uuid,
  sujet: z.string().nullable(),
  expediteur: z.string(),
  destinataires: z.string(),
  direction: z.enum(["entrant", "sortant"]),
  dateMessage: z.string().datetime({ offset: true }),
  boite: z.string(),
  cabinetId: Uuid.nullable(),
  practitionerId: Uuid.nullable(),
  rattachementRegle: z.string(), // règle §11.4 — auditable, affichée
});
export type EmailInteraction = z.infer<typeof EmailInteractionSchema>;

/** L1 (Dove 07/09) : une pièce jointe ou une image intégrée, servie par /api/v1/emails/:id/pieces/:pieceId. */
export const EmailPieceSchema = z.object({
  id: z.string(),
  nomFichier: z.string(),
  mime: z.string(),
  taille: z.number().int(),
  inline: z.boolean(),
});
export type EmailPiece = z.infer<typeof EmailPieceSchema>;

/** Le détail d'un e-mail stocké entier. L'original HTML n'est pas ici : il est servi tel quel sur /:id/original. */
export const EmailDetailSchema = z.object({
  id: z.string(),
  gmailId: z.string(),
  sujet: z.string().nullable(),
  expediteur: z.string(),
  destinataires: z.string(),
  direction: z.enum(["entrant", "sortant"]),
  dateMessage: z.string(),
  boite: z.string(),
  cabinetId: z.string().nullable(),
  practitionerId: z.string().nullable(),
  corpsTexte: z.string().nullable(),
  originalDisponible: z.boolean(),
  corpsErreur: z.string().nullable(),
  pieces: z.array(EmailPieceSchema),
});
export type EmailDetail = z.infer<typeof EmailDetailSchema>;

export function lienGmail(gmailId: string): string {
  return `https://mail.google.com/mail/u/0/#all/${gmailId}`;
}

export const EmailValidationSchema = z.object({
  id: Uuid,
  sujetApercu: z.string().nullable(), // aperçu MINIMAL (§11.5)
  expediteur: z.string(),
  boite: z.string(), // boîte d'arrivée — affichée depuis la validation multi-boîtes
  dateMessage: z.string().datetime({ offset: true }),
  candidats: z.array(
    z.object({
      libelle: z.string(),
      praticienId: Uuid.optional(),
      cabinetId: Uuid.optional(),
      explication: z.string(),
    }),
  ),
});
export type EmailValidation = z.infer<typeof EmailValidationSchema>;

/** Boîte connectée. `receptacle` (décision Dove 2026-07-31) : boîte partagée
 * d'intake thématique (Validation, Compta…) visible et validable par tous les
 * rôles §11.8 — évite de polluer les boîtes personnelles. */
export const BOITE_TYPES = ["perso", "receptacle"] as const;
export const BoiteSchema = z.object({
  gmailAddress: z.string(),
  owner: z.string(),
  type: z.enum(BOITE_TYPES),
  theme: z.string().nullable(),
  lastSyncAt: z.string().nullable(),
});
export type Boite = z.infer<typeof BoiteSchema>;

/** Changer le type/thème d'une boîte = décision structurante : admin seul. */
export function canGererBoites(roles: readonly Role[]): boolean {
  return roles.includes("admin");
}

/** §2.6/§11.5 : décider du rattachement d'un e-mail est un acte de gestion,
 * pas une lecture. `production` CONSERVÉ (boîte CFAO — validations praticiens,
 * Dove 2026-07-30) : comportement inchangé. La visibilité de boîte reste une
 * SECONDE condition, jamais remplacée par celle-ci. */
export const ROLES_VALIDER_EMAIL: readonly Role[] = ["admin", "super_admin", "secretariat", "production"];
export function canValiderEmail(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_VALIDER_EMAIL.includes(r));
}

export const EmailsListResponseSchema = z.object({
  status: EmailsStatusSchema,
  interactions: z.array(EmailInteractionSchema),
  aValider: z.array(EmailValidationSchema),
  boites: z.array(BoiteSchema),
});
export type EmailsListResponse = z.infer<typeof EmailsListResponseSchema>;

/** §11.8 : l'accès aux e-mails importés suit la classification métier — staff.
 * `production` ajouté le 2026-07-31 : la boîte partagée CFAO (validations
 * praticiens + fichiers) porte ce rôle et doit connecter/consulter sa boîte. */
export const ROLES_EMAILS: readonly Role[] = ["admin", "secretariat", "commercial", "comptabilite", "logistique", "production"];
export function canVoirEmails(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_EMAILS.includes(r));
}
